// Copyright (c) 2025-2026 NacenCheck — TracePro Compliance-Grade Trace OS.
// Licensed under the MIT License — see LICENSE file in the repo root.
// SPDX-License-Identifier: MIT

/**
 * Phát lại hàng đợi offline của service worker TỪ TRANG (SEC-R2-11, lỗi phát lại 401).
 *
 * Trước đây SW tự gửi lại các dòng trong `tracepro-offline-v2`. SW không có access
 * token (token chỉ nằm trong bộ nhớ của trang, api-gateway chỉ đọc header
 * Authorization) nên MỌI lần phát lại đều 401 — hàng đợi không bao giờ chạy được.
 *
 * Thiết kế chọn: trang phát lại. SW chỉ xếp hàng và nhắn trang
 * (`offline-queue-replay`); trang gửi bằng token trong bộ nhớ của chính nó. Token
 * KHÔNG BAO GIỜ được ghi vào IndexedDB hay gửi sang SW.
 *
 * Chỉ gửi khi CẢ BA trùng nhau: phiên của trang (`owner`), chủ trong token đang có,
 * và `activeOwner` mà SW đang giữ; và chỉ những dòng có `owner` trùng. Phiên đổi
 * giữa chừng → dừng.
 *
 * Request mang header `X-TP-Offline-Replay: 1` để fetch handler của SW không chặn
 * lại và xếp hàng nó lần nữa khi mạng lại rớt.
 */

import { ownerFromJwt, sameOwner } from "@tracepro/mobile-host";
import type { SessionOwner } from "@tracepro/mobile-host";
import { SW_ACTIVE_OWNER_KEY, SW_META_STORE, openSwQueueDb } from "./sw-queue-owner";

export const REPLAY_HEADER = "X-TP-Offline-Replay";

/** Kho → đường dẫn gốc mà SW đã chặn (cùng origin với trang). */
export const SW_REPLAY_ROUTES: Readonly<Record<string, string>> = {
  pending_journal: "/api/v1/farming-journal/entries",
  pending_events: "/api/v1/events",
};

interface Row {
  id: number;
  payload: unknown;
  queuedAt?: string;
  idempotencyKey?: string;
  owner?: SessionOwner;
  needsAuth?: boolean;
  status?: string;
}

/** Chỉ trả số lượng của phiên hiện tại, không lộ nội dung của người khác. */
export async function getSwQueueSummary(owner: SessionOwner): Promise<{ pending: number; needsReview: number }> {
  const db = await openSwQueueDb();
  try {
    let pending = 0;
    let needsReview = 0;
    for (const store of Object.keys(SW_REPLAY_ROUTES)) {
      if (!db.objectStoreNames.contains(store)) continue;
      const rows = await new Promise<Row[]>((resolve, reject) => {
        const request = db.transaction(store, "readonly").objectStore(store).getAll();
        request.onsuccess = () => resolve(request.result as Row[]);
        request.onerror = () => reject(request.error);
      });
      for (const row of rows) if (sameOwner(row.owner, owner)) {
        pending++;
        if (row.needsAuth || row.status === "CONFLICT_MANUAL" || row.status === "REJECTED") needsReview++;
      }
    }
    return { pending, needsReview };
  } finally { db.close(); }
}

export interface ReplayDeps {
  owner: SessionOwner | null;
  getToken: () => Promise<string | null>;
  /** Đọc lại phiên trước mỗi dòng; khác `owner` thì dừng. */
  currentOwner?: () => SessionOwner | null;
  fetchImpl?: typeof fetch;
  /** Báo kết quả (cùng hình tin nhắn SW cũ) — sw-registrar hiện toast. */
  notify?: (msg: Record<string, unknown>) => void;
}

export interface ReplayResult {
  sent: number;
  rejected: number;
  held: number;
  needsAuth: boolean;
  skipped?: "no-owner" | "no-token" | "token-owner-mismatch" | "sw-owner-mismatch" | "busy";
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("Offline queue transaction failed"));
    tx.onabort = () => reject(tx.error ?? new Error("Offline queue transaction aborted"));
  });
}

async function getAll(db: IDBDatabase, store: string): Promise<Row[]> {
  if (!db.objectStoreNames.contains(store)) return [];
  return new Promise((resolve, reject) => {
    const r = db.transaction(store, "readonly").objectStore(store).getAll();
    r.onsuccess = () => resolve(r.result as Row[]);
    r.onerror = () => reject(r.error);
  });
}

async function deleteRow(db: IDBDatabase, store: string, id: number) {
  const tx = db.transaction(store, "readwrite");
  tx.objectStore(store).delete(id);
  await txDone(tx);
}

async function patchRow(db: IDBDatabase, store: string, id: number, patch: Record<string, unknown>) {
  const tx = db.transaction(store, "readwrite");
  const st = tx.objectStore(store);
  const g = st.get(id);
  g.onsuccess = () => {
    if (g.result) st.put({ ...g.result, ...patch });
  };
  await txDone(tx);
}

let inFlight: Promise<ReplayResult> | null = null;

export function replaySwQueue(deps: ReplayDeps): Promise<ReplayResult> {
  if (inFlight) return inFlight.then(() => ({ sent: 0, rejected: 0, held: 0, needsAuth: false, skipped: "busy" as const }));
  const run = async (): Promise<ReplayResult> => {
    // Nhiều tab: chỉ một tab phát lại một lúc (khoá chống trùng vẫn là lớp sau cùng).
    const locks = typeof navigator !== "undefined" ? (navigator as Navigator & { locks?: LockManager }).locks : undefined;
    if (locks?.request) {
      return await locks.request("tp-offline-sw-replay", () => doReplay(deps));
    }
    return doReplay(deps);
  };
  inFlight = run().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doReplay(deps: ReplayDeps): Promise<ReplayResult> {
  const res: ReplayResult = { sent: 0, rejected: 0, held: 0, needsAuth: false };
  const owner = deps.owner;
  if (!owner) return { ...res, skipped: "no-owner" };
  const token = await deps.getToken().catch(() => null);
  if (!token) return { ...res, skipped: "no-token" };
  if (!sameOwner(ownerFromJwt(token), owner)) return { ...res, skipped: "token-owner-mismatch" };

  let db: IDBDatabase;
  try {
    db = await openSwQueueDb();
  } catch {
    return res;
  }
  try {
    const active = await new Promise<unknown>((resolve) => {
      const g = db.transaction(SW_META_STORE, "readonly").objectStore(SW_META_STORE).get(SW_ACTIVE_OWNER_KEY);
      g.onsuccess = () => resolve(g.result);
      g.onerror = () => resolve(null);
    });
    if (!sameOwner(active, owner)) return { ...res, skipped: "sw-owner-mismatch" };

    const doFetch = deps.fetchImpl ?? fetch;
    for (const [store, endpoint] of Object.entries(SW_REPLAY_ROUTES)) {
      const all = await getAll(db, store);
      const mine = all.filter((r) => sameOwner(r.owner, owner));
      res.held += all.length - mine.length;
      let sentHere = 0;
      let rejectedHere = 0;
      for (const row of mine) {
        if (deps.currentOwner && !sameOwner(deps.currentOwner(), owner)) return res;
        let resp: Response;
        try {
          resp = await doFetch(endpoint, {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              ...(row.idempotencyKey ? { "Idempotency-Key": row.idempotencyKey } : {}),
              [REPLAY_HEADER]: "1",
            },
            body: JSON.stringify(row.payload),
          });
        } catch {
          return res; // mạng lại đứt — để nguyên, lượt sau thử lại
        }
        if (resp.ok) {
          await deleteRow(db, store, row.id);
          res.sent++;
          sentHere++;
          continue;
        }
        if (resp.status === 401 || resp.status === 403) {
          await patchRow(db, store, row.id, { needsAuth: true, lastStatus: resp.status });
          res.needsAuth = true;
          deps.notify?.({ type: "offline-queue-needs-auth", store, endpoint, pending: mine.length - sentHere - rejectedHere });
          return res;
        }
        if (resp.status === 408 || resp.status === 429 || resp.status >= 500) {
          await patchRow(db, store, row.id, { lastStatus: resp.status });
          continue;
        }
        // Giữ nguyên nội dung để sửa hoặc gửi lại sau khi giải quyết xung đột.
        let body = "";
        try {
          body = (await resp.clone().text()).slice(0, 300);
        } catch {
          /* không đọc được thân */
        }
        await patchRow(db, store, row.id, { lastStatus: resp.status, status: resp.status === 409 ? "CONFLICT_MANUAL" : "REJECTED" });
        res.rejected++;
        rejectedHere++;
        deps.notify?.({ type: "offline-queue-rejected", store, endpoint, status: resp.status, body, queuedAt: row.queuedAt });
      }
      if (sentHere > 0) deps.notify?.({ type: "offline-queue-sent", store, sent: sentHere, rejected: rejectedHere });
    }
    return res;
  } finally {
    db.close();
  }
}
